import React, { useEffect, useState } from 'react';
import { Pill, Plus, Edit2 } from 'lucide-react';
import { connect } from 'react-redux';
import { MedicationsTabProps, Medication } from '../../../../../interfaces/screens/content/patientDetails';
import { MedicationForm } from '../forms/MedicationForm';
import { hideModal, showModal } from '../../../../../connected-components/modal/reducer';
import { ModalType } from '../../../../../shared/utils/enums';
import { fetchMedications as fetchMedicationsApi } from '../../../medications/list/actions';
import { addMedication as addMedicationApi } from '../../../medications/addMedication/actions';
import { store } from '../../../../../redux/store';

// Replace with your actual medication options source
const medicationList = [
  { label: 'Acetaminophen', value: 'acetaminophen' },
  { label: 'Ibuprofen', value: 'ibuprofen' },
  { label: 'Amoxicillin', value: 'amoxicillin' },
  { label: 'Lisinopril', value: 'lisinopril' },
  { label: 'Metformin', value: 'metformin' },
  { label: 'Atorvastatin', value: 'atorvastatin' },
  { label: 'Omeprazole', value: 'omeprazole' },
  { label: 'Albuterol', value: 'albuterol' },
  { label: 'Metoprolol', value: 'metoprolol' },
  { label: 'Levothyroxine', value: 'levothyroxine' },
];

// Skeleton loader for medication items
const MedicationSkeleton = () => (
  <div className="p-6 animate-pulse">
    <div className="flex items-start space-x-3">
      <div className="h-5 w-5 rounded-full bg-gray-200 dark:bg-gray-700 mt-1" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-3 w-20 bg-gray-200 dark:bg-gray-700 rounded" />
        <div className="h-3 w-40 bg-gray-200 dark:bg-gray-700 rounded" />
      </div>
    </div>
  </div>
);

const MedicationsTab: React.FC<MedicationsTabProps & { patientId: number }> = ({
  patientId,
}) => {
  const [medications, setMedications] = useState<Medication[]>([]);
  const [editingMedication, setEditingMedication] = useState<Medication | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadMedications = async () => {
      setLoading(true);
      const res = await fetchMedicationsApi(patientId);
      if (res && res.result === 'success' && res.content && res.content.data) {
        setMedications(res.content.data.map((med: any) => ({
          id: String(med.id),
          name: med.name,
          dosage: med.dosage,
          frequency: med.frequency,
          startDate: med.start_date,
          endDate: med.end_date,
          status: med.is_active ? 'active' : 'discontinued',
          notes: med.notes,
        })));
      }
      setLoading(false);
    };
    loadMedications();
  }, [patientId]);

  const getStatusColor = (status: Medication['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
      case 'discontinued':
        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
      case 'completed':
        return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
    }
  };

  const handleSubmit = async (formData: any) => {
    // Add patient_id to the form data
    const dataToSend = {
      ...formData,
      patient_id: patientId,
      is_active: 1,
    };
    const res = await addMedicationApi(dataToSend);
    if (res && res.result === 'success' && res.content) {
      setMedications((prev) => [
        {
          id: String(res.content.id),
          name: res.content.name,
          dosage: res.content.dosage,
          frequency: res.content.frequency,
          startDate: res.content.start_date,
          endDate: res.content.end_date,
          status: res.content.is_active ? 'active' : 'discontinued',
          notes: res.content.notes,
        },
        ...prev,
      ]);
    }
    setEditingMedication(null);
    store.dispatch(hideModal());
  };

  const handleAddMedication = () => {
    setEditingMedication(null);
    store.dispatch(showModal({
      type: ModalType.COMPONENT,
      title: 'Add Medication',
      component: (
        <MedicationForm
          onSubmit={handleSubmit}
          onCancel={() => setEditingMedication(null)}
          medications={medicationList}
          patientId={patientId}
        />
      ),
    }));
  };

  const activeMedications = medications.filter((med) => med.status === 'active');
  const medicationHistory = medications.filter((med) => med.status !== 'active');

  return (
    <div className="space-y-6">
      {/* Header with Add Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium dark:text-white">Patient Medications</h2>
        <button
          onClick={handleAddMedication}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-[#2823F5] dark:bg-[#4F4DFF] hover:bg-[#1f1bb8] dark:hover:bg-[#3F3DFF]"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Medication
        </button>
      </div>

      {/* Active Medications */}
      <div className="bg-white dark:bg-darkCardBackground border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-medium text-gray-900 dark:text-white">Active Medications</h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {loading ? (
            <>
              <MedicationSkeleton />
              <MedicationSkeleton />
            </>
          ) : activeMedications.length > 0 ? (
            activeMedications.map((medication) => (
              <div key={medication.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <Pill className="h-5 w-5 text-[#2823F5] dark:text-[#4F4DFF] mt-1" />
                    <div>
                      <div className="flex items-center space-x-2">
                        <h4 className="text-base font-medium text-gray-900 dark:text-white">
                          {medication.name}
                        </h4>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            medication.status
                          )}`}
                        >
                          {medication.status.charAt(0).toUpperCase() +
                            medication.status.slice(1)}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {medication.dosage} - {medication.frequency}
                      </p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Started: {new Date(medication.startDate).toLocaleDateString()}
                      </p>
                      {medication.notes && (
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{medication.notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No active medications
            </div>
          )}
        </div>
      </div>

      {/* Medication History */}
      <div className="bg-white dark:bg-darkCardBackground border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-base font-medium text-gray-900 dark:text-white">Medication History</h3>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {loading ? (
            <>
              <MedicationSkeleton />
              <MedicationSkeleton />
            </>
          ) : medicationHistory.length > 0 ? (
            medicationHistory.map((medication) => (
              <div key={medication.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <Pill className="h-5 w-5 text-gray-400 mt-1" />
                    <div>
                      <div className="flex items-center space-x-2">
                        <h4 className="text-base font-medium text-gray-900 dark:text-white">
                          {medication.name}
                        </h4>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                            medication.status
                          )}`}
                        >
                          {medication.status.charAt(0).toUpperCase() +
                            medication.status.slice(1)}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        {medication.dosage} - {medication.frequency}
                      </p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Started: {new Date(medication.startDate).toLocaleDateString()}
                      </p>
                      {medication.notes && (
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{medication.notes}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No medication history
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MedicationsTab; 